import { tokenizeFoodText } from '@cf-shared/food-text-tokenize';
import { FoodTextAnalyzerDeps } from '@cf-shared/food-search/food-search-match';
import { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';

/**
 * Staple rows in the Add Foods typing suggestions (food staples plan 4b, #5624).
 *
 * A staple is one of our curated plain foods (`food/$STP-<number>`). While a person types, the
 * staples whose name or alias holds every typed word are offered as rows of their own, above the
 * ordinary suggestion lines. The rows are matched here, on the device, against a list compiled
 * into the app; plan 4c (#5625) replaces that list with a synced staples database.
 *
 * Only clean matches count: a typo shows no staple row. The server's suggestion lines still mend
 * typos, and picking one of those still finds the staple in the search results.
 *
 * A plain module rather than an Angular service, so it loads under the node unit-test runner.
 */

/** how many staple rows are shown at most */
export const STAPLE_SUGGEST_ROW_COUNT = 2;

/** the shortest typed text that gets staple rows; the same limit as the other suggestion rows */
export const STAPLE_SUGGEST_MIN_TEXT_LENGTH = 2;

/** one staple as the matcher receives it */
export interface StapleNames {
  /** the food id, `food/$STP-<number>` */
  id: string;
  /** the name shown on the row */
  name: string;
  /** the other names the staple is found by; the first is what picking the row searches */
  aliases: readonly string[];
  /** how often people searched for this kind of food; higher ranks first. Absent counts as 0 */
  popularity?: number;
  /** the order the curator set within the staple's group, 1 first. Absent counts as 1 */
  groupOrder?: number;
}

/** one name of a staple, split into words once */
interface PreparedName {
  /** the words as typed-text is compared against them for a prefix: lowercased, accents folded */
  words: string[];
  /** the same words stemmed, for whole-word comparison */
  stems: string[];
  /** the name's length in characters, an ordering rule */
  length: number;
  /** true for the staple's own name, false for an alias */
  isOwnName: boolean;
}

interface PreparedStaple {
  staple: StapleNames;
  names: PreparedName[];
  /** the staple's place in the list, the last ordering rule */
  order: number;
}

/** how a staple matched, best first */
enum StapleMatchKind {
  /** its own name holds every typed word and starts with one of them ("Chicken breast, …" for "chicken") */
  NameStart = 0,
  /** its own name holds every typed word elsewhere ("Butter, salted" for "sal") */
  Name = 1,
  /** only an alias holds them ("Egg, whole, raw" through "chicken egg") */
  Alias = 2,
}

/** the best way one staple matched what was typed */
interface StapleMatch {
  prepared: PreparedStaple;
  /** how the staple matched, the first ordering rule */
  matchKind: StapleMatchKind;
  /** words in the best-matching name beyond the typed ones */
  extraWords: number;
  length: number;
}

/**
 * The order of staple rows (owner, 2026-09-17, after trying the first version on dev):
 * 1. a staple whose own name starts with a typed word, then one whose own name holds the typed
 *    words elsewhere, then one matched only by an alias — so "chicken" does not offer
 *    "Egg, whole, raw" through its alias "chicken egg", and "sal" offers salmon before salted butter;
 * 2. the kind of food people search for most (the group's search count);
 * 3. the curator's order within that group, so "raw" comes before "roasted";
 * 4. fewest extra words, then the shorter name, then the staple's place in the list.
 */
function compareStapleMatches(a: StapleMatch, b: StapleMatch): number {
  return a.matchKind - b.matchKind
    || (b.prepared.staple.popularity ?? 0) - (a.prepared.staple.popularity ?? 0)
    || (a.prepared.staple.groupOrder ?? 1) - (b.prepared.staple.groupOrder ?? 1)
    || a.extraWords - b.extraWords
    || a.length - b.length
    || a.prepared.order - b.prepared.order;
}

/**
 * Finds the staple rows for typed text. Build it once: the staple names are split and stemmed on
 * the first call and kept, because they never change while the app runs.
 */
export class StapleSuggester {
  private prepared: PreparedStaple[] | undefined = undefined;

  constructor(
    private readonly staples: readonly StapleNames[],
    private readonly analyzer: FoodTextAnalyzerDeps,
  ) { }

  /** The staple rows for `text`, best first, at most `maxRows` of them. */
  rowsFor(text: string, maxRows: number = STAPLE_SUGGEST_ROW_COUNT): SuggestionRow[] {
    if (text.trim().length < STAPLE_SUGGEST_MIN_TEXT_LENGTH) {
      return [];
    }
    const typedWords = this.wordsOf(text);
    if (typedWords.length === 0) {
      return [];
    }
    const typedStems = typedWords.map(word => this.analyzer.stem(word));
    // the last word is still being typed unless the text ends with a space
    const lastWordFinished = /\s$/.test(text);
    const matches: StapleMatch[] = [];
    for (const prepared of this.preparedStaples()) {
      const best = this.bestMatch(prepared, typedWords, typedStems, lastWordFinished);
      if (best) {
        matches.push(best);
      }
    }
    matches.sort(compareStapleMatches);
    return matches.slice(0, maxRows).map(match => {
      const staple = match.prepared.staple;
      // picking searches the short name: the full name's "peeled" and "raw" would find every
      // other peeled or raw staple instead of foods like this one
      return { text: staple.name, stapleId: staple.id, stapleSearchText: staple.aliases[0] ?? staple.name };
    });
  }

  /** the best-matching name of one staple, or undefined when none holds every typed word */
  private bestMatch(prepared: PreparedStaple, typedWords: string[], typedStems: string[], lastWordFinished: boolean): StapleMatch | undefined {
    let best: StapleMatch | undefined = undefined;
    let matchKind = StapleMatchKind.Alias;
    for (const name of prepared.names) {
      if (!this.nameHoldsEveryWord(name, typedWords, typedStems, lastWordFinished)) {
        continue;
      }
      if (name.isOwnName) {
        matchKind = this.startsWithATypedWord(name, typedWords, typedStems, lastWordFinished) ? StapleMatchKind.NameStart : StapleMatchKind.Name;
      }
      const extraWords = Math.max(0, name.words.length - typedWords.length);
      // if this name has fewer extra words, or as few and is shorter, it is the better match
      if (!best || extraWords < best.extraWords || (extraWords === best.extraWords && name.length < best.length)) {
        best = { prepared, matchKind: StapleMatchKind.Alias, extraWords, length: name.length };
      }
    }
    // if the own name matched, the staple counts as a name match whichever name fits best
    if (best) {
      best.matchKind = matchKind;
    }
    return best;
  }

  /** does the name's first word match one of the typed words, by the same rules as below? */
  private startsWithATypedWord(name: PreparedName, typedWords: string[], typedStems: string[], lastWordFinished: boolean): boolean {
    const lastIndex = typedWords.length - 1;
    return typedStems.includes(name.stems[0])
      || (!lastWordFinished && name.words[0].startsWith(typedWords[lastIndex]));
  }

  /**
   * Does the name hold every typed word? A finished word must have the same stem as one of the
   * name's words. The word still being typed may instead be the start of one of them.
   */
  private nameHoldsEveryWord(name: PreparedName, typedWords: string[], typedStems: string[], lastWordFinished: boolean): boolean {
    for (let i = 0; i < typedWords.length; i++) {
      const sameStem = name.stems.includes(typedStems[i]);
      const isLastWord = i === typedWords.length - 1;
      // if this is the word still being typed, a name word that starts with it also counts
      const startsAWord = isLastWord && !lastWordFinished && name.words.some(word => word.startsWith(typedWords[i]));
      if (!sameStem && !startsAWord) {
        return false;
      }
    }
    return true;
  }

  /** every staple with its names split and stemmed, worked out on the first call and kept */
  private preparedStaples(): PreparedStaple[] {
    if (!this.prepared) {
      this.prepared = this.staples.map((staple, order) => ({
        staple,
        order,
        names: [staple.name, ...staple.aliases]
          .map((text, index) => this.prepareName(text, index === 0))
          .filter(name => name.words.length > 0),
      }));
    }
    return this.prepared;
  }

  /** one name split into words, each also stemmed */
  private prepareName(text: string, isOwnName: boolean): PreparedName {
    const words = this.wordsOf(text);
    return { words, stems: words.map(word => this.analyzer.stem(word)), length: text.length, isOwnName };
  }

  /** the words of a text, lowercased and accent-folded, the way the food search splits them */
  private wordsOf(text: string): string[] {
    return tokenizeFoodText(text, this.analyzer.foldAccents).filter(word => word.length > 0);
  }
}
